// RFC 9562 UUIDv7: 48-bit ms timestamp | 4-bit version (0x7) | 12-bit rand_a | 2-bit variant (0b10) | 62-bit rand_b.
// Lexicographic order across same-process calls is preserved by a per-process tiebreaker counter that
// nudges the random tail forward when the millisecond hasn't advanced.

let lastMs = 0;
let lastTail = 0n;

export function uuidv7(): string {
  const now = Date.now();
  const ms = now > lastMs ? now : lastMs;

  const randA = crypto.getRandomValues(new Uint16Array(1))[0] & 0x0fff;       // 12 bits
  const randBHi = crypto.getRandomValues(new Uint32Array(1))[0] & 0x3fffffff; // 30 bits (top 30 of 62-bit rand_b)
  const randBLo = crypto.getRandomValues(new Uint32Array(1))[0];               // 32 bits (bottom 32 of 62-bit rand_b)

  // Pack into a single bigint for tiebreak comparison: randA(12) | randBHi(30) | randBLo(32) = 74 bits.
  let tail = (BigInt(randA) << 62n) | (BigInt(randBHi) << 32n) | BigInt(randBLo);

  if (ms === lastMs && tail <= lastTail) {
    tail = lastTail + 1n;
  }
  lastMs = ms;
  lastTail = tail;

  // Extract fields back from tail.
  const rA = Number((tail >> 62n) & 0x0fffn);                 // 12-bit rand_a
  const rBHi = Number((tail >> 32n) & 0x3fffffffn);           // 30-bit top of rand_b
  const rBLo = Number(tail & 0xffffffffn);                    // 32-bit bottom of rand_b

  const msHex = ms.toString(16).padStart(12, "0");
  const timeLow = msHex.slice(0, 8);                          // time_low: bits 47-16 of timestamp
  const timeMid = msHex.slice(8, 12);                         // time_mid: bits 15-0 of timestamp

  // version nibble (7) | rand_a (12 bits) → 16-bit field
  const verAndRandA = (0x7000 | rA).toString(16).padStart(4, "0");

  // variant bits (10) | top 14 bits of rand_b → 16-bit field
  // rBHi is 30 bits; top 14 bits = rBHi >> 16
  const variantField = (0x8000 | (rBHi >> 16)).toString(16).padStart(4, "0");

  // bottom 16 bits of rBHi (16 bits) + rBLo (32 bits) → 48-bit node field
  const rBHiLow16 = (rBHi & 0xffff).toString(16).padStart(4, "0");
  const rBLoHex = rBLo.toString(16).padStart(8, "0");

  return `${timeLow}-${timeMid}-${verAndRandA}-${variantField}-${rBHiLow16}${rBLoHex}`;
}
